import {getLocalizationBasedOnLang} from '../../data/models/User/UserInfo';
import {roundWait} from './queue';
import type {JobQueueLane} from './types';

/*
 * What authors read while Studio is too busy to start their work at once: the queue card on
 * the storyboard and videos pages, the library card's chip, the wait shown beside Generate,
 * and failures that pass on their own (Studio's AI service at capacity, Studio too busy).
 */

const englishWait = (seconds: number) => {
  const wait = roundWait(seconds);
  if (wait === 'underMinute') return 'less than a minute';
  if (!wait.hours) return `about ${wait.minutes} min`;
  return wait.minutes
    ? `about ${wait.hours} h ${wait.minutes} min`
    : `about ${wait.hours} h`;
};

const welshWait = (seconds: number) => {
  const wait = roundWait(seconds);
  if (wait === 'underMinute') return 'llai na munud';
  if (!wait.hours) return `tua ${wait.minutes} munud`;
  return wait.minutes
    ? `tua ${wait.hours} awr ${wait.minutes} munud`
    : `tua ${wait.hours} awr`;
};

const english = {
  /** A rounded wait: "less than a minute", "about 6 min", "about 1 h 20 min", "about 3 h". */
  wait: englishWait,
  // The card's heading: what the work waits for. The plan lane also makes a video's voice,
  // pictures and previews (planVideo).
  title: {
    plan: 'Waiting to start your storyboard',
    planVideo: 'Waiting to start making your video',
    document: 'Waiting for the document reader',
    export: 'Waiting to export your video',
    provider: "Waiting for Studio's AI service",
    unknown: "Waiting in Studio's queue",
  } as Record<JobQueueLane | 'planVideo' | 'unknown', string>,
  ahead: (count: number) =>
    count <= 0 ? "You're next" : `${count} ahead of you`,
  startsIn: (seconds: number) => `Starts in ${englishWait(seconds)}`,
  estimateOverdue: 'Taking longer than estimated',
  waited: (seconds: number) => `Waiting for ${englishWait(seconds)}`,
  leave:
    'You can leave this page. Your work keeps its place and continues automatically.',
  // While the MP4 waits to export, the previews made already play.
  previewPlays: 'The preview plays now; only the MP4 waits to export.',
  provider:
    "Studio's AI service is at capacity right now. Your video will continue automatically.",
  // The library card's chip.
  chip: 'Queued',
  chipAhead: (count: number) =>
    count <= 0 ? 'Queued · next' : `Queued · ${count} ahead`,
  chipLabel: (title: string, facts: string) =>
    facts ? `${title}: ${facts}` : title,
  // Beside Generate while Studio is busy.
  busyWait: (seconds: number) =>
    `Studio is busy: your video may wait ${englishWait(seconds)} to start.`,
  busyQueue: (count: number) =>
    `Studio is busy: ${count} ${
      count === 1 ? 'video is' : 'videos are'
    } waiting to export.`,
  busy: 'Studio is busy, so your video may take a little longer to start.',
  // Failures that pass: generating continues from where it stopped.
  providerCapacityFailed:
    "Studio's AI service was at capacity, so this stopped before it finished. Nothing is lost: press Continue to pick up where it left off.",
  transientFailed:
    'Studio was busy and stopped this before it finished. Nothing is lost: press Continue to pick up where it left off.',
};

const welsh: typeof english = {
  wait: welshWait,
  title: {
    plan: 'Yn aros i ddechrau eich bwrdd stori',
    planVideo: 'Yn aros i ddechrau gwneud eich fideo',
    document: 'Yn aros am y darllenydd dogfennau',
    export: 'Yn aros i allforio eich fideo',
    provider: 'Yn aros am wasanaeth AI Studio',
    unknown: 'Yn aros yng nghiw Studio',
  },
  ahead: (count: number) =>
    count <= 0 ? 'Chi sydd nesaf' : `${count} o’ch blaen`,
  startsIn: (seconds: number) => `Yn dechrau mewn ${welshWait(seconds)}`,
  estimateOverdue: 'Yn cymryd mwy o amser na’r amcangyfrif',
  waited: (seconds: number) => `Wedi aros ${welshWait(seconds)}`,
  leave:
    'Gallwch adael y dudalen hon. Mae eich gwaith yn cadw ei le ac yn parhau’n awtomatig.',
  previewPlays:
    'Mae’r rhagolwg yn chwarae nawr; dim ond yr MP4 sy’n aros i gael ei allforio.',
  provider:
    'Mae gwasanaeth AI Studio yn llawn ar hyn o bryd. Bydd eich fideo yn parhau’n awtomatig.',
  chip: 'Mewn ciw',
  chipAhead: (count: number) =>
    count <= 0 ? 'Mewn ciw · nesaf' : `Mewn ciw · ${count} o’ch blaen`,
  chipLabel: (title: string, facts: string) =>
    facts ? `${title}: ${facts}` : title,
  busyWait: (seconds: number) =>
    `Mae Studio yn brysur: efallai y bydd eich fideo yn aros ${welshWait(
      seconds,
    )} cyn dechrau.`,
  busyQueue: (count: number) =>
    `Mae Studio yn brysur: mae ${count} fideo yn aros i gael eu hallforio.`,
  busy: 'Mae Studio yn brysur, felly gall gymryd ychydig yn hirach i’ch fideo ddechrau.',
  providerCapacityFailed:
    'Roedd gwasanaeth AI Studio yn llawn, felly daeth hyn i ben cyn gorffen. Does dim wedi’i golli: pwyswch Parhau i ailgychwyn o’r man lle stopiodd.',
  transientFailed:
    'Roedd Studio yn brysur a daeth hyn i ben cyn gorffen. Does dim wedi’i golli: pwyswch Parhau i ailgychwyn o’r man lle stopiodd.',
};

export const queueText: typeof english = getLocalizationBasedOnLang(
  english,
  welsh,
);
